import styles from "./../../styles/charts/chartStyle.module.css";
import { useEffect, useState } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Paper,
  Modal,
  Box,
  Typography,
} from "@mui/material";
import { useAllFundNames, usePropertyData } from "../../lib/hooks";
import BarChart from "./protoCharts/BarChart";
import {
  getChartBottomIcons,
  getChartInfoIcons,
} from "./twitter/TwitterWordCloud";
import { roundNumber } from "../../lib/util";
import { desc } from "../../lib/chartDescription";

interface MaxMinAvgFundsProps {
  xlabel: string;
  ylabel: string;
  single_fund: boolean;
  property_type?: "Amount" | "Rating" | "Votes" | "Proposal";
  // type?: "List" | "Max" | "Min" | "Avg" | "Count" | "Sum";
  chartType?: "boxWhisker" | "bar";
  // For amount we need some type but for rating we may not need same , so passing an array from the parent
  typeArray?: string[];

  // need to remove the following props when property_type bug is fixed from backend to accept Proposal
  isProposal?: boolean;
}

const voteArray = [
  { name: "Up Votes", val: "YesVotes" },
  { name: "Down Votes", val: "NoVotes" },
  { name: "Result Votes", val: "ResultVotes" },
];

const MaxMinAvgFundsChart = (props: MaxMinAvgFundsProps) => {
  const {
    ylabel,
    single_fund,
    // type = "List",
    chartType = "boxWhisker",
    property_type = "Amount",
    typeArray = ["Avg", "Max", "Min", "Sum"],
    isProposal = false,
  } = props;

  const [fund, setFund] = useState<any>(4);

  const [listType, setListType] = useState<
    "Max" | "Min" | "Avg" | "Sum" | "Count" | string
  >(typeArray.length > 0 ? typeArray[0] : "Avg");

  const [status_grouping, setStatusGrouping] = useState<
    "None" | "Binary" | "Ternary"
  >("Ternary");

  const [voteType, setVoteType] = useState<
    "YesVotes" | "NoVotes" | "ResultVotes"
  >("YesVotes");

  const { data, loading } = usePropertyData({
    property_name:
      property_type !== "Votes" ? (property_type as "Amount") : voteType,
    aggregation_type: listType as "Max" | "Min" | "Avg" | "Sum" | "Count",
    status_grouping: status_grouping,
    proposal_status: undefined,
    fund_id: single_fund ? fund : undefined,
  });

  const {
    allFundNames,
    loading: fundNameLoading,
    error: fundNameError,
  } = useAllFundNames();

  useEffect(() => {
    !fundNameLoading &&
      fundNameError === null &&
      setFund(allFundNames.slice(-1)[0].fund_id);
    //eslint-disable-next-line
  }, [fundNameLoading]);

  const handleChange = (event: any) => {
    setFund(event.target.value);
  };

  const handleChangeStatus = (event: any) => {
    setStatusGrouping(event.target.value);
  };

  const handleChangeVotes = (event: any) => {
    setVoteType(event.target.value);
  };

  const handleChangeType = (event: any) => {
    setListType(event.target.value);
  };

  let chartData: any[] = [];
  let categories: any[] = [];

  if (loading) {
    chartData = [];
  } else {
    switch (chartType) {
      case "boxWhisker":
        if (single_fund) {
          data.forEach((item: any) => {
            chartData.push({
              x: item.challenge_name,
              y: roundNumber(item.value),
            });
            categories.push(item.challenge_name);
          });
        } else {
          data.forEach((item: any) => {
            chartData.push({ x: item.fund_id, y: roundNumber(item.value) });
            categories.push(item.fund_name);
          });
        }
        break;
      case "bar":
        let data1: any[] = [];
        let data2: any[] = [];
        let data3: any[] = [];
        if (status_grouping === "Ternary") {
          data.forEach((item: any) => {
            data1.push(roundNumber(item.items.funded_value));
            data2.push(roundNumber(item.items.overbudget_value));
            data3.push(roundNumber(item.items.not_approved_value));
            props.single_fund
              ? categories.push(item.challenge_name)
              : categories.push(item.fund_name);
          });
          chartData = [data1, data2, data3];
        } else {
          data.forEach((item: any) => {
            data1.push(roundNumber(item.items.funded_value));
            data2.push(roundNumber(item.items.not_funded_value));
            props.single_fund
              ? categories.push(item.challenge_name)
              : categories.push(item.fund_name);
          });
          chartData = [data1, data2];
        }
        break;
      default:
        break;
    }
  }

  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };

  const content = (showMaxButton: boolean) => {
    return (
      <>
        <Paper elevation={3} className={styles.chartContainer}>
          <div className="horizontalCenter">
            <Typography className={styles.chartHeader}>
              {isProposal
                ? "Proposal Count in Different Funds"
                : `${listType} of ${property_type} in Different ${
                    single_fund ? "Challenges" : "Funds"
                  }`}
            </Typography>
          </div>
          <div className="horizontalCenter">
            <div className={styles.selectOptionRow}>
              {single_fund && (
                <FormControl size="small">
                  <InputLabel className={styles.flexSelectFont}>
                    Fund
                  </InputLabel>
                  <Select
                    className={styles.flexSelectFont}
                    value={fund}
                    label="Fund"
                    onChange={handleChange}
                    defaultValue=""
                    size="small"
                    sx={{ width: 120 }}

                    // sx={{ width: 100 }}
                  >
                    {allFundNames.map((fund: any) => (
                      <MenuItem
                        style={{ fontSize: "14px" }}
                        value={fund.fund_id}
                        key={`fund-variation-${fund.fund_id}`}
                      >
                        {fund.fund_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {typeArray.length > 1 && (
                <FormControl size="small">
                  <InputLabel className={styles.flexSelectFont}>
                    Aggregation
                  </InputLabel>
                  <Select
                    className={styles.flexSelectFont}
                    value={listType}
                    label="Aggregation"
                    onChange={handleChangeType}
                    defaultValue="Max"
                    size="small"
                    sx={{ width: 120 }}
                  >
                    {typeArray.map((fund: any) => (
                      <MenuItem
                        style={{ fontSize: "14px" }}
                        value={fund}
                        key={`fund-variation-${fund}`}
                      >
                        {fund}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {property_type === "Votes" && (
                <FormControl size="small">
                  <InputLabel className={styles.flexSelectFont}>
                    Vote Type
                  </InputLabel>
                  <Select
                    value={voteType}
                    label="Vote Type"
                    onChange={handleChangeVotes}
                    defaultValue=""
                    size="small"
                    className={styles.flexSelectFont}
                    sx={{ width: 120 }}
                  >
                    {voteArray.map((vote: any) => (
                      <MenuItem
                        style={{ fontSize: "14px" }}
                        value={vote.val}
                        key={`fund-variation-${vote.val}`}
                      >
                        {vote.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {
                <FormControl size="small">
                  <InputLabel className={styles.flexSelectFont}>
                    Grouping Status
                  </InputLabel>
                  <Select
                    value={status_grouping}
                    label="Grouping Status"
                    onChange={handleChangeStatus}
                    defaultValue=""
                    size="small"
                    className={styles.flexSelectFont}
                    sx={{ width: 120 }}
                  >
                    {["Binary", "Ternary"].map((vote: any) => (
                      <MenuItem
                        style={{ fontSize: "14px" }}
                        value={vote}
                        key={`fund-variation-${vote}`}
                      >
                        {vote}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              }
            </div>
          </div>

          {
            <div style={{ width: "100%" }}>
              {chartType === "bar" && (
                <BarChart
                  loading={loading}
                  numberOfColumns={3}
                  chartData={chartData}
                  legend={
                    status_grouping === "Ternary"
                      ? ["Funded", "Overbudget", "Not Approved"]
                      : ["Funded", "Not Funded"]
                  }
                  // xLabel={xlabel}
                  yLabel={ylabel}
                  categories={categories}
                  isDollar={property_type === "Amount"}
                />
              )}
            </div>
          }
          {getChartBottomIcons(showMaxButton, setOpen)}
          {getChartInfoIcons(
            desc["minMaxAvg"][property_type as "Amount"][
              !single_fund ? "fund" : "challenge"
            ]
          )}
        </Paper>
      </>
    );
  };
  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className={styles.fullScreenChart}>{content(false)}</Box>
      </Modal>
      {content(true)}
    </>
  );
};

export default MaxMinAvgFundsChart;
